import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";

import { Subject } from "rxjs";
import { WebcamImage, WebcamInitError, WebcamModule, WebcamUtil } from "ngx-webcam";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ToastService } from "../../services/shared/ToastService.service";

/**
 * Webcam capture widget (ngx-webcam) that emits the snapshot through `webcamImage`; prefers the rear camera when several exist.
 * `width`/`height` size the preview, `hideButton` hides the capture button and `videoOptions` sets media constraints. Init errors show an error toast.
 */
@Component({
  selector: "app-web-camera",
  templateUrl: "./web-camera.component.html",
  styleUrls: ["./web-camera.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslateModule, WebcamModule]
})
export class WebCameraComponent implements OnInit, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  showWebcam: boolean;
  @Input() height: number;
  @Input() width: number;
  @Input() hideButton: boolean;
  @Input() videoOptions: MediaTrackConstraints;
  multipleWebcamsAvailable: boolean = false;
  errors: WebcamInitError[] = [];
  @ViewChild("webcamContainer") webcamContainer: ElementRef;
  cameraWidth: number;
  cameraHeight: number;
  cameraPadding: number = 10;

  @Output() webcamImage = new EventEmitter<WebcamImage>();

  // ngx-webcam observes this subject and captures a frame when it emits.
  trigger: Subject<void> = new Subject<void>();
  backCamera: any;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    WebcamUtil.getAvailableVideoInputs().then((mediaDevices: MediaDeviceInfo[]) => {
      this.multipleWebcamsAvailable = mediaDevices && mediaDevices.length > 1;
      if (this.multipleWebcamsAvailable) {
        this.backCamera = mediaDevices.find(f => /back|rear|environment/gi.test(f.label)); // Default Back Facing Camera
        this.videoOptions.deviceId = this.backCamera.deviceId;
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Post-render execution
  /***************************************************************************/

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.webcamContainer.nativeElement.style.setProperty(
        "--webcam-container-width",
        this.width + "px"
      );
      this.webcamContainer.nativeElement.style.setProperty(
        "--webcam-container-height",
        this.height + "px"
      );
      this.webcamContainer.nativeElement.style.setProperty(
        "--webcam-container-padding",
        this.cameraPadding + "px"
      );
      this.cameraWidth = this.width;
      this.cameraHeight = this.height;
      this.showWebcam = true;
    }, 0);
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  triggerSnapshot(): void {
    this.trigger.next();
  }

  toggleWebcam(): void {
    this.showWebcam = !this.showWebcam;
  }

  handleInitError(error: WebcamInitError): void {
    this.errors.push(error);
    this.ToastService.fireToast("error", this.translate.instant("web-camera-init-error"));
  }

  handleImage(webcamImage: WebcamImage): void {
    this.webcamImage.emit(webcamImage);
  }
}
